/**
 * UserService
 * Service for User entity operations
 * Route: api/users
 */
import { BaseService } from '../base/BaseService';
/**
 * UserService
 * Handles user CRUD operations and user-specific queries
 */
export class UserService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? UserService.DEFAULT_SERVICE_PATH,
            basePath: UserService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL (Override with pagination) ====================
    /**
     * Get all users with pagination
     * GET api/users?page=1&pageSize=50
     */
    async getAll(params) {
        const response = await this.getAllRaw(params);
        return response.data ?? [];
    }
    /**
     * Get all users with full response
     */
    getAllRaw(params) {
        const url = this.buildUrlWithParams(undefined, params);
        return this.http.get(url);
    }
    // ==================== GET BY ID (Override for string ID) ====================
    /**
     * Get user by ID
     * GET api/users/{id}
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`User ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get user by ID with full response
     */
    getByIdRaw(id) {
        const url = this.buildUrl(id);
        return this.http.get(url);
    }
    // ==================== GET VIEW ====================
    /**
     * Get users view with pagination
     * GET api/users/view?page=1&pageSize=50
     */
    async getView(params) {
        const response = await this.getViewRaw(params);
        return response.data ?? [];
    }
    /**
     * Get users view with full response
     */
    getViewRaw(params) {
        const endpoint = this.getEndpoint('view', 'view');
        const url = this.buildUrlWithParams(endpoint, params);
        return this.http.get(url);
    }
    // ==================== GET USER VIEW BY ID ====================
    /**
     * Get user view by ID
     * GET api/users/view/{id}
     */
    async getUserView(id) {
        const response = await this.getUserViewRaw(id);
        if (!response.data) {
            throw new Error(`User ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get user view by ID with full response
     */
    getUserViewRaw(id) {
        const endpoint = this.getEndpoint('view-id', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== GET CURRENT USER (ME) ====================
    /**
     * Get current user profile
     * GET api/users/me
     */
    async getCurrentUser() {
        const response = await this.getCurrentUserRaw();
        if (!response.data) {
            throw new Error('Current user not found');
        }
        return response.data;
    }
    /**
     * Get current user profile with full response
     */
    getCurrentUserRaw() {
        const endpoint = this.getEndpoint('me', 'me');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== UPDATE CURRENT USER (ME) ====================
    /**
     * Update current user profile
     * PUT api/users/me
     */
    async updateCurrentUser(data) {
        const response = await this.updateCurrentUserRaw(data);
        if (!response.data) {
            throw new Error('Failed to update current user');
        }
        return response.data;
    }
    /**
     * Update current user profile with full response
     */
    updateCurrentUserRaw(data) {
        const endpoint = this.getEndpoint('me', 'me');
        const url = this.buildUrl(endpoint);
        return this.http.put(url, data);
    }
    // ==================== UPDATE AVATAR ====================
    /**
     * Update current user's avatar
     * PUT api/users/me/avatar
     * @param avatarUrl The URL of the uploaded avatar image (from S3 or other storage)
     */
    async updateMyAvatar(avatarUrl) {
        const response = await this.updateMyAvatarRaw({ avatar: avatarUrl });
        if (!response.data) {
            throw new Error('Failed to update avatar');
        }
        return response.data;
    }
    /**
     * Update current user's avatar with full response
     */
    updateMyAvatarRaw(data) {
        const endpoint = this.getEndpoint('me-avatar', 'me/avatar');
        const url = this.buildUrl(endpoint);
        return this.http.put(url, data);
    }
    // ==================== UPDATE THUMBNAIL ====================
    /**
     * Update current user's thumbnail
     * PUT api/users/me/thumbnail
     * @param thumbnailUrl The URL of the uploaded thumbnail image (from S3 or other storage)
     */
    async updateMyThumbnail(thumbnailUrl) {
        const response = await this.updateMyThumbnailRaw({ thumbnail: thumbnailUrl });
        if (!response.data) {
            throw new Error('Failed to update thumbnail');
        }
        return response.data;
    }
    /**
     * Update current user's thumbnail with full response
     */
    updateMyThumbnailRaw(data) {
        const endpoint = this.getEndpoint('me-thumbnail', 'me/thumbnail');
        const url = this.buildUrl(endpoint);
        return this.http.put(url, data);
    }
    // ==================== ASSIGN ROLES ====================
    /**
     * Assign roles to user
     * POST api/users/{id}/roles
     */
    async assignRoles(userId, data) {
        const response = await this.assignRolesRaw(userId, data);
        return response.success;
    }
    /**
     * Assign roles to user with full response
     */
    assignRolesRaw(userId, data) {
        const endpoint = this.getEndpoint('roles', `${userId}/roles`);
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    // ==================== GET USER ROLES ====================
    /**
     * Get user roles
     * GET api/users/{id}/roles
     */
    async getUserRoles(userId) {
        const response = await this.getUserRolesRaw(userId);
        return response.data ?? [];
    }
    /**
     * Get user roles with full response
     */
    getUserRolesRaw(userId) {
        const endpoint = this.getEndpoint('get-roles', `${userId}/roles`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== GET USER PERMISSIONS ====================
    /**
     * Get user permissions
     * GET api/users/{id}/permissions
     */
    async getUserPermissions(userId) {
        const response = await this.getUserPermissionsRaw(userId);
        return response.data ?? [];
    }
    /**
     * Get user permissions with full response
     */
    getUserPermissionsRaw(userId) {
        const endpoint = this.getEndpoint('permissions', `${userId}/permissions`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== SEARCH USERS ====================
    /**
     * Search users by name, email, or username
     * GET api/users/search?searchTerm=john&page=1&pageSize=50
     */
    async search(params) {
        const response = await this.searchRaw(params);
        return response.data ?? [];
    }
    /**
     * Search users with full response
     */
    searchRaw(params) {
        const endpoint = this.getEndpoint('search', 'search');
        const url = this.buildUrlWithParams(endpoint, params);
        return this.http.get(url);
    }
    // ==================== CREATE USER ====================
    /**
     * Create a new user
     * POST api/users
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create user');
        }
        return response.data;
    }
    /**
     * Create a new user with full response
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE USER ====================
    /**
     * Update existing user
     * PUT api/users/{id}
     */
    async update(id, data) {
        const response = await this.updateRaw(id, data);
        if (!response.data) {
            throw new Error('Failed to update user');
        }
        return response.data;
    }
    /**
     * Update existing user with full response
     */
    updateRaw(id, data) {
        const url = this.buildUrl(id);
        return this.http.put(url, data);
    }
    // ==================== DELETE USER ====================
    /**
     * Delete user
     * DELETE api/users/{id}
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Delete user with full response
     */
    deleteRaw(id) {
        const url = this.buildUrl(id);
        return this.http.delete(url);
    }
    // ==================== USER PUSH DEVICES ====================
    /**
     * Get current user's registered devices
     * GET api/users/me/devices
     */
    async getMyDevices() {
        const response = await this.getMyDevicesRaw();
        return response.data ?? [];
    }
    /**
     * Get current user's registered devices with full response
     */
    getMyDevicesRaw() {
        const endpoint = this.getEndpoint('my-devices', 'me/devices');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Register a new device for current user
     * POST api/users/me/devices
     */
    async registerMyDevice(data) {
        const response = await this.registerMyDeviceRaw(data);
        if (!response.data) {
            throw new Error('Failed to register device');
        }
        return response.data;
    }
    /**
     * Register a new device for current user with full response
     */
    registerMyDeviceRaw(data) {
        const endpoint = this.getEndpoint('my-devices', 'me/devices');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    /**
     * Update a device for current user
     * PUT api/users/me/devices/{deviceId}
     */
    async updateMyDevice(deviceId, data) {
        const response = await this.updateMyDeviceRaw(deviceId, data);
        if (!response.data) {
            throw new Error('Failed to update device');
        }
        return response.data;
    }
    /**
     * Update a device for current user with full response
     */
    updateMyDeviceRaw(deviceId, data) {
        const endpoint = this.getEndpoint('my-device', `me/devices/${deviceId}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, data);
    }
    /**
     * Delete a device for current user
     * DELETE api/users/me/devices/{deviceId}
     */
    async deleteMyDevice(deviceId) {
        const response = await this.deleteMyDeviceRaw(deviceId);
        return response.success;
    }
    /**
     * Delete a device for current user with full response
     */
    deleteMyDeviceRaw(deviceId) {
        const endpoint = this.getEndpoint('my-device', `me/devices/${deviceId}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
    // ==================== USER PREFERENCES ====================
    /**
     * Get current user's preferences for a specific app
     * GET api/users/me/preferences/{appId}
     */
    async getMyPreferences(appId) {
        const response = await this.getMyPreferencesRaw(appId);
        if (!response.data) {
            throw new Error(`Preferences for app ${appId} not found`);
        }
        return response.data;
    }
    /**
     * Get current user's preferences for a specific app with full response
     */
    getMyPreferencesRaw(appId) {
        const endpoint = this.getEndpoint('my-preferences-app', `me/preferences/${appId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all preferences for current user
     * GET api/users/me/preferences
     */
    async getAllMyPreferences() {
        const response = await this.getAllMyPreferencesRaw();
        return response.data ?? [];
    }
    /**
     * Get all preferences for current user with full response
     */
    getAllMyPreferencesRaw() {
        const endpoint = this.getEndpoint('my-preferences', 'me/preferences');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Create or update current user's preferences for a specific app
     * POST api/users/me/preferences
     */
    async saveMyPreferences(data) {
        const response = await this.saveMyPreferencesRaw(data);
        if (!response.data) {
            throw new Error('Failed to save preferences');
        }
        return response.data;
    }
    /**
     * Create or update current user's preferences for a specific app with full response
     */
    saveMyPreferencesRaw(data) {
        const endpoint = this.getEndpoint('my-preferences', 'me/preferences');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    /**
     * Update current user's preferences for a specific app
     * PUT api/users/me/preferences/{appId}
     */
    async updateMyPreferences(appId, data) {
        const response = await this.updateMyPreferencesRaw(appId, data);
        if (!response.data) {
            throw new Error('Failed to update preferences');
        }
        return response.data;
    }
    /**
     * Update current user's preferences for a specific app with full response
     */
    updateMyPreferencesRaw(appId, data) {
        const endpoint = this.getEndpoint('my-preferences-app', `me/preferences/${appId}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, data);
    }
    /**
     * Delete current user's preferences for a specific app
     * DELETE api/users/me/preferences/{appId}
     */
    async deleteMyPreferences(appId) {
        const response = await this.deleteMyPreferencesRaw(appId);
        return response.success;
    }
    /**
     * Delete current user's preferences for a specific app with full response
     */
    deleteMyPreferencesRaw(appId) {
        const endpoint = this.getEndpoint('my-preferences-app', `me/preferences/${appId}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
    // ==================== USER AUDIT & ANALYTICS ====================
    /**
     * Get current user's login history
     * GET api/users/me/login-history?page=1&pageSize=20
     */
    async getMyLoginHistory(params) {
        const response = await this.getMyLoginHistoryRaw(params);
        return response.data ?? [];
    }
    /**
     * Get current user's login history with full response
     */
    getMyLoginHistoryRaw(params) {
        const endpoint = this.getEndpoint('my-login-history', 'me/login-history');
        const url = this.buildUrlWithParams(endpoint, params);
        return this.http.get(url);
    }
    /**
     * Get current user's activity statistics
     * GET api/users/me/analytics
     */
    async getMyAnalytics() {
        const response = await this.getMyAnalyticsRaw();
        return response.data ?? {};
    }
    /**
     * Get current user's activity statistics with full response
     */
    getMyAnalyticsRaw() {
        const endpoint = this.getEndpoint('my-analytics', 'me/analytics');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
UserService.DEFAULT_SERVICE_PATH = 'users';
UserService.BASE_PATH = 'api/users';
//# sourceMappingURL=UserService.js.map